const RELATIVE_TIME_UNITS = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
] as const;
const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

export interface AppBuildMetadata {
  commitSha?: unknown;
  commitSubject?: unknown;
  builtAt?: unknown;
}

function normalizeCommitSha(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const commitSha = value.trim().toLowerCase();
  return /^[0-9a-f]{7,64}$/.test(commitSha) ? commitSha : null;
}

function normalizeCommitSubject(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const subject = value.replace(/\s+/g, ' ').trim();
  return subject && subject !== 'unknown' ? subject : null;
}

function normalizeBuildTimestamp(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const builtAt = value.trim();
  return Number.isFinite(Date.parse(builtAt)) ? builtAt : null;
}

export function formatBuildAge(value: unknown, now = Date.now()): string | null {
  const builtAt = normalizeBuildTimestamp(value);
  if (!builtAt) return null;

  const difference = Date.parse(builtAt) - now;
  if (Math.abs(difference) < 45 * 1000) return 'just now';

  const selectedUnit =
    RELATIVE_TIME_UNITS.find(([, unitMilliseconds]) => Math.abs(difference) >= unitMilliseconds) ??
    RELATIVE_TIME_UNITS[RELATIVE_TIME_UNITS.length - 1]!;
  const [unit, milliseconds] = selectedUnit;

  return RELATIVE_TIME_FORMATTER.format(Math.round(difference / milliseconds), unit);
}

export function presentAppBuildIdentity(
  {
    commitSha: commitShaValue,
    commitSubject: commitSubjectValue,
    builtAt: builtAtValue,
  }: AppBuildMetadata = {},
  now = Date.now(),
) {
  const commitSha = normalizeCommitSha(commitShaValue);
  const commitSubject = normalizeCommitSubject(commitSubjectValue);
  const builtAt = normalizeBuildTimestamp(builtAtValue);
  const buildAge = formatBuildAge(builtAt, now);
  const exactBuildTime = builtAt ? new Date(builtAt).toLocaleString() : null;

  return {
    commitSha,
    commitLabel: commitSha ? `Commit ${commitSha.slice(0, 7)}` : 'Commit unavailable',
    commitSubject: commitSubject || 'Commit message unavailable',
    builtAt,
    buildAgeLabel: buildAge ? `Built ${buildAge}` : 'Build time unavailable',
    title: [
      commitSha ? `Full commit: ${commitSha}` : 'No commit ID was embedded in this build',
      commitSubject
        ? `Commit message: ${commitSubject}`
        : 'No commit message was embedded in this build',
      exactBuildTime ? `Built: ${exactBuildTime}` : 'No build time was embedded in this build',
    ].join('\n'),
  };
}

const embeddedCommitSha =
  typeof __APP_BUILD_COMMIT_SHA__ === 'string' ? __APP_BUILD_COMMIT_SHA__ : '';
const embeddedCommitSubject =
  typeof __APP_BUILD_COMMIT_SUBJECT__ === 'string' ? __APP_BUILD_COMMIT_SUBJECT__ : '';
const embeddedBuildTimestamp =
  typeof __APP_BUILD_TIMESTAMP__ === 'string' ? __APP_BUILD_TIMESTAMP__ : '';

export const embeddedAppBuildMetadata = {
  commitSha: embeddedCommitSha,
  commitSubject: embeddedCommitSubject,
  builtAt: embeddedBuildTimestamp,
};
